import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

interface DashboardBarChartCardProps {
  title: string;
  data: Array<Record<string, any>>;
  xDataKey: string;
  barDataKey?: string;
  barName: string;
  barColor?: string;
  barCategoryGap?: string | number;
  xAxisAngle?: number;
  xAxisHeight?: number;
  minPointSize?: number;
  chartHeight?: number;
  getCellFill?: (entry: any, index: number) => string;
}

export function DashboardBarChartCard({
  title,
  data,
  xDataKey,
  barDataKey = 'count',
  barName,
  barColor = '#003883',
  barCategoryGap = '20%',
  xAxisAngle = -20,
  xAxisHeight = 80,
  minPointSize = 6,
  chartHeight = 350,
  getCellFill
}: DashboardBarChartCardProps) {
  const formatTooltipValue = (value: any) => {
    if (barDataKey !== 'totalQuoteValue') {
      return value;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? `$ ${numericValue.toLocaleString()}`
      : value;
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} barCategoryGap={barCategoryGap}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey={xDataKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#666' }}
            interval={0}
            angle={xAxisAngle}
            textAnchor="end"
            height={xAxisHeight}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            domain={[0, 'dataMax + 1']}
            allowDecimals={false}
            tick={{ fontSize: 12, fill: '#666' }}
          />
          <Tooltip
            formatter={(value: any) => [formatTooltipValue(value), barName]}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
          <Legend />
          <Bar dataKey={barDataKey} minPointSize={minPointSize} fill={barColor} radius={[4, 4, 0, 0]} name={barName}>
            {getCellFill && data.map((entry, index) => (
              <Cell key={`${xDataKey}-${index}`} fill={getCellFill(entry, index)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
